import { createContext, useState, useContext, useCallback } from 'react';
import Api from 'entities/helpers/api';
import ErrorLogger from 'entities/helpers/error-log-helper';
import PropTypes from 'prop-types';
import { useIdentity } from 'base/identity-resolver';
import { createEmailSearchFunction } from '../utils/email-search-utils';

const EmailThreadsContext = createContext({});

const LIMIT = 20;

const EmailThreadsProvider = ({ children }) => {
  const [threads, setThreads] = useState([]);
  const [threadTotal, setThreadTotal] = useState(0);
  const [hasEverHadData, setHasEverHadData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const { user = {} } = useIdentity();

  const getAllSentByUser = async () => {
    setLoading(true);
    try {
      const res = await Api.OUTREACH.EMAIL_THREADS.get({
        params: {
          offset: (page - 1) * LIMIT,
          limit: LIMIT,
        },
      });
      const { items, total } = res;
      setThreads(items);
      setThreadTotal(total);
      if (items?.length > 0) {
        setHasEverHadData(true);
      }
    } catch (e) {
      ErrorLogger.error('Error getting sent emails', { extra: { e, page_name: 'Outreach Sent Emails' } });
    } finally {
      setLoading(false);
    }
  };

  const updateSent = async (payload, id) => {
    try {
      const res = await Api.OUTREACH.EMAIL.put({
        urlArgs: { id },
        data: JSON.stringify(payload),
      });
      setThreads(threads.map((thread) => (thread.id === id ? res : thread)));
    } catch (e) {
      ErrorLogger.error('Error updating sent email', { extra: { e, page_name: 'Outreach Sent Emails' } });
    }
  };

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const searchThreads = useCallback(
    async (filters) => {
      return createEmailSearchFunction('sent', user, setThreads, setThreadTotal, page, LIMIT, setLoading)(filters);
    },
    [page, user, setThreads, setThreadTotal, setLoading]
  );

  return (
    <EmailThreadsContext.Provider
      value={{
        threads,
        loading,
        page,
        onPageChange,
        threadTotal,
        threadLimit: LIMIT,
        updateSent,
        getAllSentByUser,
        searchThreads,
        hasEverHadData,
      }}
    >
      {children}
    </EmailThreadsContext.Provider>
  );
};

const useEmailThreads = () => {
  const context = useContext(EmailThreadsContext);
  if (!context) {
    throw new Error('useEmailThreads must be used within a EmailThreadsProvider');
  }
  return context;
};

EmailThreadsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { EmailThreadsProvider, useEmailThreads };
